/**
 * Entrance and exit presets (spec 016): a `MotionPreset` becomes ordinary
 * keyframes, one way. `motionPresetKeyframes` expands a preset for one
 * orientation, `presetAsAuthored` for every orientation as authored keys,
 * and `applyMotionPreset` writes them onto a sprite's track through 006's
 * write rules (`keyframeWrite.ts`). The sprite panel's Motion section (via
 * the store's `applySpriteMotionPreset`) and the assistant's
 * `setSpriteMotion` (021) call the writer. Nothing reads keyframes back as a
 * preset. Pure and deterministic; never reads a store.
 */

import type { Animation, AnimationType, Keyframe, Orientation, Position, Sprite, Transform } from '../@types';
import { toKeyframeEasing } from './interpolate';
import { getResponsivePosition, getResponsiveTransform } from './responsiveLayout';
import { forkOrientation, isUntaggedKeyframe, putKeyframe, type NewKeyframeId } from './keyframeWrite';

/** An entrance placed by its start time, an exit by its end time (ms). */
export interface MotionPreset {
  enter?: { animation: Animation; atMs: number };
  exit?: { animation: Animation; endMs: number };
}

/** Default durations (ms) when an `Animation` gives none (D-3). */
export const DEFAULT_ANIMATION_DURATION_MS: Readonly<Record<AnimationType, number>> = Object.freeze({
  none: 0,
  fade: 500,
  slide: 400,
  scale: 400,
  bounce: 600,
  spin: 500,
  custom: 400,
});

/** "Fly from …" starts this far (% of the canvas dimension) outside the edge (D-1). */
const DEFAULT_SLIDE_OFFSET = 110;

/** An instant change is a 1 ms step: two keyframes at distinct whole-ms times. */
const CUT_MS = 1;

/** The properties a preset writes; the rest pose keys all five. */
export const MOTION_PROPERTIES = ['opacity', 'position.x', 'position.y', 'transform.scale', 'transform.rotation'] as const;

type MotionSprite = Pick<Sprite, 'layout' | 'transform'>;

/** A sprite's rest value of a motion property in `orientation` (undefined for any other property). */
export function motionRestValue(sprite: MotionSprite, property: string, orientation: Orientation): number | undefined {
  switch (property) {
    case 'opacity': return 1;
    case 'position.x': return getResponsivePosition(sprite.layout, orientation).x;
    case 'position.y': return getResponsivePosition(sprite.layout, orientation).y;
    case 'transform.scale': return getResponsiveTransform(sprite.layout, orientation, sprite.transform).scale;
    case 'transform.rotation': return getResponsiveTransform(sprite.layout, orientation, sprite.transform).rotation || 0;
    default: return undefined;
  }
}

/** An animation's duration (ms): its own when a finite number ≥ 0, else the table's. */
export function motionDurationMs(anim: Animation): number {
  return typeof anim.duration === 'number' && Number.isFinite(anim.duration) && anim.duration >= 0
    ? Math.round(anim.duration)
    : DEFAULT_ANIMATION_DURATION_MS[anim.type] ?? 0;
}

interface PropValue { property: string; value: number }

function restProps(pos: Position, tr: Transform): PropValue[] {
  return [
    { property: 'opacity', value: 1 },
    { property: 'position.x', value: pos.x },
    { property: 'position.y', value: pos.y },
    { property: 'transform.scale', value: tr.scale },
    { property: 'transform.rotation', value: tr.rotation || 0 },
  ];
}

function slideProps(direction: 'left' | 'right' | 'up' | 'down', pos: Position, offset?: number): PropValue[] {
  const dist = typeof offset === 'number' && Number.isFinite(offset) ? offset : DEFAULT_SLIDE_OFFSET;
  const dx = direction === 'left' ? -dist : direction === 'right' ? dist : 0;
  const dy = direction === 'up' ? -dist : direction === 'down' ? dist : 0;
  return [
    { property: 'position.x', value: pos.x + dx },
    { property: 'position.y', value: pos.y + dy },
  ];
}

function enterFromProps(anim: Animation, pos: Position, tr: Transform): PropValue[] {
  switch (anim.type) {
    case 'fade': return [{ property: 'opacity', value: 0 }];
    case 'scale':
    case 'bounce': return [{ property: 'opacity', value: 0 }, { property: 'transform.scale', value: 0 }];
    case 'spin': return [{ property: 'opacity', value: 0 }, { property: 'transform.rotation', value: (tr.rotation || 0) - 360 }];
    case 'slide': return [{ property: 'opacity', value: 1 }, ...slideProps(anim.direction ?? 'left', pos, anim.offset)];
    default: return [{ property: 'opacity', value: 0 }];
  }
}

/** The exit's end pose. A fly-out stays opaque; the expansion hides it at the end. */
function exitToProps(anim: Animation, pos: Position, tr: Transform): PropValue[] {
  switch (anim.type) {
    case 'fade': return [{ property: 'opacity', value: 0 }];
    case 'scale': return [{ property: 'opacity', value: 0 }, { property: 'transform.scale', value: 0 }];
    case 'spin': return [{ property: 'opacity', value: 0 }, { property: 'transform.rotation', value: (tr.rotation || 0) + 360 }];
    case 'slide': return slideProps(anim.direction ?? 'right', pos, anim.offset);
    default: return [{ property: 'opacity', value: 0 }];
  }
}

const wholeMs = (v: number): number => (Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0);

/**
 * The keyframes that play `preset` in `orientation`, each tagged with it, at
 * absolute values from the sprite's rest pose there. Ids are `mp-<n>`,
 * numbered per call (callers writing to a track give their own).
 */
export function motionPresetKeyframes(sprite: MotionSprite, preset: MotionPreset, orientation: Orientation): Keyframe[] {
  const out: Keyframe[] = [];
  let n = 0;
  const emit = (time: number, props: PropValue[], easing?: Keyframe['easing']) => {
    const t = wholeMs(time);
    for (const pv of props) {
      const same = out.findIndex((k) => k.time === t && k.property === pv.property);
      if (same >= 0) out.splice(same, 1);
      out.push({ id: `mp-${++n}`, time: t, property: pv.property, value: pv.value, ...(easing ? { easing } : {}), orientation });
    }
  };
  const opacity = (value: number): PropValue[] => [{ property: 'opacity', value }];

  const pos = getResponsivePosition(sprite.layout, orientation);
  const tr = getResponsiveTransform(sprite.layout, orientation, sprite.transform);
  const rest = restProps(pos, tr);

  let enterEnd = 0;
  const enter = preset.enter;
  if (enter) {
    const anim = enter.animation;
    const enterStart = wholeMs(enter.atMs);
    if (anim.type === 'none') {
      enterEnd = enterStart;
      if (enterStart > 0) {
        emit(enterStart - CUT_MS, opacity(0));
        emit(enterStart, opacity(1));
      }
    } else {
      enterEnd = enterStart + motionDurationMs(anim);
      const from = enterFromProps(anim, pos, tr);
      const fromOpacity = from.find((p) => p.property === 'opacity')?.value;
      if (enterStart > 0 && fromOpacity !== 0) emit(enterStart - CUT_MS, opacity(0));
      emit(enterStart, from, toKeyframeEasing(anim.ease));
      emit(enterEnd, rest);
    }
  }

  const exit = preset.exit;
  if (exit) {
    const anim = exit.animation;
    const exitEnd = wholeMs(exit.endMs);
    if (anim.type === 'none') {
      emit(Math.max(enterEnd, exitEnd - CUT_MS), opacity(1));
      emit(exitEnd, opacity(0));
    } else {
      const exitStart = Math.max(enterEnd, 0, exitEnd - motionDurationMs(anim));
      const to = exitToProps(anim, pos, tr);
      // The earlier keyframe's easing shapes a segment, so the ease sits on the rest pose.
      emit(exitStart, rest, toKeyframeEasing(anim.ease));
      emit(exitEnd, to);
      if (!to.some((p) => p.property === 'opacity')) {
        emit(Math.max(exitStart, exitEnd - CUT_MS), opacity(1));
        emit(exitEnd, opacity(0));
      }
    }
  }

  return out;
}

const shapeOf = (kfs: readonly Keyframe[]) =>
  JSON.stringify(kfs.map((k) => [k.time, k.property, k.value, k.easing ?? null]));

/**
 * The preset as authored keyframes for every orientation: the desktop set
 * untagged, and for portrait and landscape a tagged set wherever theirs
 * differs from desktop's, with one resting keyframe (time 0) for a property
 * desktop animates and that orientation does not. Ids are left to the
 * caller (placeholders `mp-…`).
 */
export function presetAsAuthored(sprite: MotionSprite, preset: MotionPreset): Keyframe[] {
  const strip = (kfs: Keyframe[]): Keyframe[] => kfs.map(({ orientation: _o, ...rest }) => rest);
  const desktop = strip(motionPresetKeyframes(sprite, preset, 'desktop'));
  const out: Keyframe[] = [...desktop];
  for (const o of ['portrait', 'landscape'] as const) {
    const own = motionPresetKeyframes(sprite, preset, o);
    if (shapeOf(own) === shapeOf(desktop)) continue;
    const ownProps = new Set(own.map((k) => k.property));
    out.push(...own);
    for (const property of new Set(desktop.map((k) => k.property))) {
      if (ownProps.has(property)) continue;
      const value = motionRestValue(sprite, property, o);
      if (value !== undefined) out.push({ id: `mp-rest-${property}-${o}`, time: 0, property, value, orientation: o });
    }
  }
  return out;
}

export interface ApplyMotionPresetOptions {
  /** Absent or `desktop`: every orientation (`presetAsAuthored`); portrait or landscape: that one only. */
  orientation?: Orientation;
  newId: NewKeyframeId;
}

/** The orientation set a keyframe belongs to: `''` for untagged (and desktop-tagged), else its tag. */
const setOf = (kf: Pick<Keyframe, 'orientation'>): string =>
  !kf.orientation || kf.orientation === 'desktop' ? '' : kf.orientation;

/**
 * Write `preset` onto a sprite's track and return the new `Keyframe[]`.
 * Before writing, each existing keyframe of a written property, in a written
 * orientation set, whose time lies within that property's written span
 * (first to last written time, inclusive) is removed (D-6); everything else
 * is kept. Tagged writes fork the property's untagged keys first (006).
 * New keyframes get ids from `newId`; untagged ones carry no `orientation`.
 */
export function applyMotionPreset(
  track: readonly Keyframe[],
  sprite: MotionSprite,
  preset: MotionPreset,
  { orientation, newId }: ApplyMotionPresetOptions,
): Keyframe[] {
  const tagged = orientation === 'portrait' || orientation === 'landscape' ? orientation : undefined;
  const written: Keyframe[] = tagged
    ? motionPresetKeyframes(sprite, preset, tagged)
    : presetAsAuthored(sprite, preset);
  if (written.length === 0) return [...track];

  let next: Keyframe[] = [...track];
  if (tagged) {
    for (const property of new Set(written.map((k) => k.property))) {
      next = forkOrientation(next, property, tagged, newId).keyframes;
    }
  }

  // Spans per (orientation set, property).
  const spans = new Map<string, { set: string; property: string; from: number; to: number }>();
  for (const k of written) {
    const key = `${setOf(k)}|${k.property}`;
    const span = spans.get(key);
    if (span) {
      span.from = Math.min(span.from, k.time);
      span.to = Math.max(span.to, k.time);
    } else {
      spans.set(key, { set: setOf(k), property: k.property, from: k.time, to: k.time });
    }
  }
  next = next.filter((k) => {
    const span = spans.get(`${setOf(k)}|${k.property}`);
    if (!span) return true;
    if (span.set === '' && !isUntaggedKeyframe(k)) return true;
    return k.time < span.from || k.time > span.to;
  });

  for (const k of written) {
    const kf: Keyframe = { ...k, id: newId() };
    if (!kf.orientation || kf.orientation === 'desktop') delete kf.orientation;
    next = putKeyframe(next, kf);
  }
  return next;
}
